// src/pages/testimonials/TestimonialsPage.js
import { useState } from 'react';
import { useQuery } from 'react-query';
import { fetchTestimonials } from '../../services/api';
import { useLang } from '../../i18n/LanguageContext';
import { SectionHeader, SkeletonGrid, ErrorBox, EmptyState, StarRating, Avatar } from '../../components/ui';
import styles from './TestimonialsPage.module.css';

function TestimonialCard({ item }) {
  const { t } = useLang();
  return (
    <div className={`${styles.card} card`}>
      <StarRating rating={item.rating} />
      <p className={styles.text}>«{t(item, 'text')}»</p>
      {item.product_title_fa && (
        <div className={styles.product}>🕯️ {t(item, 'product_title')}</div>
      )}
      <div className={styles.author}>
        <Avatar name={item.avatar_initial || item.name} color={item.avatar_color} size={42} />
        <div>
          <div className={styles.name}>{item.name}</div>
          <div className={styles.city}>{t(item, 'city')}</div>
        </div>
      </div>
    </div>
  );
}

export default function TestimonialsPage() {
  const { ui, lang } = useLang();
  const [ratingFilter, setRatingFilter] = useState(null);
  const [page, setPage] = useState(1);

  const params = {
    page,
    ...(ratingFilter && { rating: ratingFilter }),
  };

  const { data, isLoading, isError, refetch } = useQuery(
    ['testimonials', params],
    () => fetchTestimonials(params),
    { keepPreviousData: true }
  );

  const items = data?.results || data || [];
  const totalPages = data?.count ? Math.ceil(data.count / 12) : 1;

  return (
    <main className={styles.main}>
      <SectionHeader
        badge={ui('✦ رضایت‌مندی مشتریان', '✦ Customer Reviews')}
        title={ui('صدای مشتریان ما', 'Voices of Our Customers')}
        sub={ui('آنچه مشتریان درباره شمع‌های کیوت کندل می‌گویند', 'What customers say about Cute Candels')}
      />

      {/* Rating filter */}
      <div className={styles.ratingFilter}>
        <button
          onClick={() => { setRatingFilter(null); setPage(1); }}
          className={`${styles.ratingBtn} ${!ratingFilter ? styles.active : ''}`}
        >
          {ui('همه', 'All')}
        </button>
        {[5, 4, 3].map((r) => (
          <button
            key={r}
            onClick={() => { setRatingFilter(r); setPage(1); }}
            className={`${styles.ratingBtn} ${ratingFilter === r ? styles.active : ''}`}
          >
            {'★'.repeat(r)}
          </button>
        ))}
      </div>

      {isLoading && <SkeletonGrid count={6} height={220} />}
      {isError && <ErrorBox onRetry={refetch} />}
      {!isLoading && !isError && items.length === 0 && (
        <EmptyState icon="💬" fa="نظری یافت نشد" en="No reviews found" lang={lang} />
      )}
      {!isLoading && !isError && items.length > 0 && (
        <div className={styles.grid}>
          {items.map((item) => <TestimonialCard key={item.id} item={item} />)}
        </div>
      )}

      {totalPages > 1 && (
        <div className={styles.pagination}>
          <button className="btn-secondary" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>
            {ui('قبلی', 'Prev')}
          </button>
          <span style={{ color: 'var(--text-muted)', fontSize: '0.88rem' }}>{page} / {totalPages}</span>
          <button className="btn-secondary" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages}>
            {ui('بعدی', 'Next')}
          </button>
        </div>
      )}
    </main>
  );
}
